import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterSection } from '../components/dashboard/DashboardFilterModal';
import { useStoreRegions } from './useStoreRegions';

/**
 * The four STORE-attribute filters — Trade, Category, Region, City — shared by the
 * report pages, so every report scopes by store the same way and Region resolves
 * exactly as it does on the Dashboard (see useStoreRegions).
 *
 * A page supplies only the accessors its rows can answer, and only those filters are
 * offered: a report whose API doesn't send the store's trade yet simply has no Trade
 * control, instead of a dropdown that silently matches nothing.
 *
 * Options are built from the rows themselves (the house pattern on Store Visits /
 * Mood), so a dropdown never offers a value the report has no rows for. That makes
 * this CLIENT-side filtering — correct only for reports that hold the whole result
 * set in the browser. A server-paged report (Grooming) must filter server-side
 * instead, or it would only ever filter the page you can see.
 */
const ALL = 'all';
/** Stores with a blank trade — selectable on purpose, so they can be found and fixed. */
export const NO_TRADE = '__none__';

export interface StoreAttrAccessors<T> {
  trade?: (row: T) => string | null | undefined;
  category?: (row: T) => string | null | undefined;
  city?: (row: T) => string | null | undefined;
  /** Region is derived from the state — there is no region column. */
  state?: (row: T) => string | null | undefined;
}

export interface StoreAttrFilters<T> {
  /** Drop into the page's DashboardFilterModal `sections` array. */
  sections: FilterSection[];
  /** True when the row passes every set filter. */
  matches: (row: T) => boolean;
  activeCount: number;
  /** True when at least one of the four is set — for "is this list scoped?" checks. */
  isActive: boolean;
  clear: () => void;
}

const uniqSorted = (values: Array<string | null | undefined>): string[] =>
  [...new Set(values.map((v) => (v ?? '').trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );

export function useStoreAttrFilters<T>(rows: readonly T[], get: StoreAttrAccessors<T>): StoreAttrFilters<T> {
  const [trade, setTrade] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [city, setCity] = useState(ALL);
  const { regionOf, regionsFrom } = useStoreRegions();

  // Accessors are inline literals (new identity every render), so they are read through
  // a ref: the real inputs are the ROWS and the selected values, and depending on the
  // literals would rebuild every option list on every render.
  const getRef = useRef(get);
  getRef.current = get;

  const tradeOptions = useMemo(() => (getRef.current.trade ? uniqSorted(rows.map(getRef.current.trade)) : []), [rows]);
  const categoryOptions = useMemo(() => (getRef.current.category ? uniqSorted(rows.map(getRef.current.category)) : []), [rows]);
  const cityOptions = useMemo(() => (getRef.current.city ? uniqSorted(rows.map(getRef.current.city)) : []), [rows]);
  const regionOptions = useMemo(
    () => (getRef.current.state ? regionsFrom(rows.map(getRef.current.state)) : []),
    [rows, regionsFrom],
  );

  // A selection the data no longer offers (new date range, project switch) would hide
  // every row with no way back except Clear all — drop it instead.
  useEffect(() => { if (trade !== ALL && trade !== NO_TRADE && !tradeOptions.includes(trade)) setTrade(ALL); }, [tradeOptions, trade]);
  useEffect(() => { if (category !== ALL && !categoryOptions.includes(category)) setCategory(ALL); }, [categoryOptions, category]);
  useEffect(() => { if (city !== ALL && !cityOptions.includes(city)) setCity(ALL); }, [cityOptions, city]);
  useEffect(() => { if (region !== ALL && !regionOptions.includes(region)) setRegion(ALL); }, [regionOptions, region]);

  const matches = useCallback(
    (row: T): boolean => {
      const g = getRef.current;
      if (trade !== ALL && g.trade) {
        const v = (g.trade(row) ?? '').trim();
        if (trade === NO_TRADE ? v !== '' : v !== trade) return false;
      }
      if (category !== ALL && g.category && (g.category(row) ?? '').trim() !== category) return false;
      if (city !== ALL && g.city && (g.city(row) ?? '').trim() !== city) return false;
      if (region !== ALL && g.state && regionOf(g.state(row)) !== region) return false;
      return true;
    },
    [trade, category, city, region, regionOf],
  );

  // Trade → Category → Region → City: store attributes from coarsest grouping to
  // finest location, and Region before City for the same reason the Dashboard does it.
  const sections = useMemo(() => {
    const out: FilterSection[] = [];
    if (getRef.current.trade) {
      out.push({
        kind: 'select', key: 'trade', label: 'Store Trade', value: trade, onChange: setTrade,
        options: [
          { value: ALL, label: 'All trades' },
          ...tradeOptions.map((t) => ({ value: t, label: t })),
          { value: NO_TRADE, label: '— No trade set —' },
        ],
      });
    }
    if (getRef.current.category) {
      out.push({
        kind: 'select', key: 'storeCategory', label: 'Store Category', value: category, onChange: setCategory,
        options: [{ value: ALL, label: 'All categories' }, ...categoryOptions.map((c) => ({ value: c, label: c }))],
      });
    }
    if (getRef.current.state) {
      out.push({
        kind: 'select', key: 'region', label: 'Region', value: region, onChange: setRegion,
        options: [{ value: ALL, label: 'All regions' }, ...regionOptions.map((r) => ({ value: r, label: r }))],
      });
    }
    if (getRef.current.city) {
      out.push({
        kind: 'select', key: 'storeCity', label: 'City', value: city, onChange: setCity,
        options: [{ value: ALL, label: 'All cities' }, ...cityOptions.map((c) => ({ value: c, label: c }))],
      });
    }
    return out;
  }, [trade, category, region, city, tradeOptions, categoryOptions, regionOptions, cityOptions]);

  const activeCount = [trade, category, region, city].filter((v) => v !== ALL).length;

  const clear = useCallback(() => { setTrade(ALL); setCategory(ALL); setRegion(ALL); setCity(ALL); }, []);

  return { sections, matches, activeCount, isActive: activeCount > 0, clear };
}
